import { computed } from "vue";
import type { Component } from "vue";
import { storeToRefs } from "pinia";
import { BellRing, Bookmark, Building2, Database, Download, Sparkles } from "lucide-vue-next";
import { UCC_COMPANY_OVERVIEW_CREDIT_TYPE, UCC_RECOMMENDATIONS_CREDIT_TYPE, useEndpointPermissions } from "@/composables/useEndpointPermissions";
import { useFollowedCompanyCount } from "@/composables/useFollowedCompanyCount";
import { useSavedCompanyCount } from "@/composables/useSavedCompanyCount";
import { useSavedFilingCount } from "@/composables/useSavedFilingCount";
import { useUser } from "@/stores/user";
import { hasMonitoringAccess } from "@/utils/monitoringAccess";
import IconUccSearch from "@/components/icon/UccSearch.vue";

export type NavItem = {
	id: string;
	label: string;
	to: string;
	icon: Component;
	badge?: string;
	badgeAriaLabel?: string;
};

// One source for the app's primary destinations. The desktop sidebar, the phone
// tab bar and the phone menu sheet all render this list, so access gating and
// the Saved and Monitoring badges can't drift between them.
export function useNavItems() {
	const userStore = useUser();
	const { user } = storeToRefs(userStore);
	const route = useRoute();

	const canAccessCompanies = computed(() => user.value?.access_companies === true);
	const canAccessUcc = computed(() => user.value?.access_ucc === true);
	// access_ucc is the UCC credit type alone; the monitoring workspace also needs
	// the UCC_COMPANIES wallet that backs the company overview it links into.
	// The permissions and company counts are loaded on the
	// server by middleware/auth.global.ts, next to the user, so the nav is
	// complete on first paint. The client-side loads below only matter when a
	// client-side navigation reaches this component before any server render
	// has filled them (a login that stays inside the SPA).
	const { loadEndpointPermissions, hasCreditTypeAccess } = useEndpointPermissions();
	const canAccessCompanyOverview = computed(() => hasCreditTypeAccess(UCC_COMPANY_OVERVIEW_CREDIT_TYPE));
	const canAccessRecommendations = computed(() => canAccessUcc.value && hasCreditTypeAccess(UCC_RECOMMENDATIONS_CREDIT_TYPE));
	const canAccessMonitoring = computed(() => hasMonitoringAccess(user.value) && canAccessCompanyOverview.value);
	const canAccessExports = computed(() => canAccessCompanies.value || canAccessUcc.value);
	const { followedCompanyCount, followedCompanyCountLoaded, loadFollowedCompanyCount } = useFollowedCompanyCount();
	const { savedCompanyCount, savedCompanyCountLoaded, loadSavedCompanyCount } = useSavedCompanyCount();
	const { savedFilingCount, savedFilingCountLoaded, loadSavedFilingCount } = useSavedFilingCount();
	const savedCount = computed(() => savedCompanyCount.value + savedFilingCount.value);

	const savedBadge = computed(() => {
		const count = savedCount.value;
		if (count <= 0) return undefined;
		return count > 99 ? "99+" : String(count);
	});

	const monitoringBadge = computed(() => {
		const count = followedCompanyCount.value;
		if (count <= 0) return undefined;
		return count > 99 ? "99+" : String(count);
	});

	const items = computed<NavItem[]>(() => {
		const list: NavItem[] = [];
		if (canAccessCompanies.value) list.push({ id: "companies", label: "Companies", to: "/companies", icon: Building2 });
		if (canAccessUcc.value) list.push({ id: "ucc", label: "UCC", to: "/ucc", icon: IconUccSearch });
		if (canAccessRecommendations.value) list.push({ id: "recommendations", label: "Recommendations", to: "/ucc/recommendations", icon: Sparkles });
		if (canAccessUcc.value) {
			list.push({
				id: "saved-companies",
				label: "Saved",
				to: "/ucc/saved-companies",
				icon: Bookmark,
				badge: savedBadge.value,
				badgeAriaLabel: savedBadge.value ? `Saved, ${savedCompanyCount.value} ${savedCompanyCount.value === 1 ? "company" : "companies"} and ${savedFilingCount.value} ${savedFilingCount.value === 1 ? "filing" : "filings"}` : undefined,
			});
		}
		if (canAccessMonitoring.value) {
			list.push({
				id: "monitoring",
				label: "Monitoring",
				to: "/ucc/monitoring",
				icon: BellRing,
				badge: monitoringBadge.value,
				badgeAriaLabel: monitoringBadge.value ? (followedCompanyCount.value === 1 ? "Monitoring, 1 company" : `Monitoring, ${followedCompanyCount.value} companies`) : undefined,
			});
		}
		list.push({ id: "enrichment", label: "Enrichment", to: "/enrichment", icon: Database });
		if (canAccessExports.value) list.push({ id: "exports", label: "Exports", to: "/exports", icon: Download });
		return list;
	});

	// One item is current: the visible destination whose path is the longest
	// prefix of the route, so /ucc/monitoring lights Monitoring, not UCC.
	const activeId = computed(() => {
		const path = route.path;
		let best: NavItem | null = null;
		for (const item of items.value) {
			const matches = path === item.to || path.startsWith(`${item.to}/`);
			if (!matches) continue;
			if (!best || item.to.length > best.to.length) best = item;
		}
		return best?.id ?? null;
	});

	function ensureLoaded() {
		void loadEndpointPermissions();
	}

	watchEffect(() => {
		if (!process.client || followedCompanyCountLoaded.value) return;
		if (!canAccessMonitoring.value || !user.value?.id || !userStore.token) return;
		void loadFollowedCompanyCount();
	});

	watchEffect(() => {
		if (!process.client || savedCompanyCountLoaded.value) return;
		if (!canAccessCompanyOverview.value || !user.value?.id || !userStore.token) return;
		void loadSavedCompanyCount();
	});
	watch([canAccessUcc, () => user.value?.id, () => userStore.token], () => {
		if (process.client && canAccessUcc.value && !savedFilingCountLoaded.value && userStore.token) void loadSavedFilingCount();
	}, { immediate: true });

	return { items, activeId, ensureLoaded };
}
